import {
  MenuNotificationAction,
  type Notification,
  type NotificationEvent,
  NotificationType,
} from '@/store/notifications/notifications.dto';
import { toInAppPath } from '@/utils/notification-redirect';

import type {
  NotificationBatch,
  NotificationDateFilter,
  NotificationItem,
  NotificationKind,
} from './types';

export const batchByTitle = (items: NotificationItem[]): NotificationBatch[] => {
  const batches: NotificationBatch[] = [];
  for (const item of items) {
    const last = batches[batches.length - 1];
    const lastHasInvite = !!last?.items[0]?.invite;
    const canBatch =
      last &&
      last.title === item.title &&
      last.kind === item.kind &&
      last.senderRole === item.senderRole &&
      !item.invite &&
      !lastHasInvite;
    if (canBatch && last) {
      last.items.push(item);
      if (item.danger) last.danger = true;
    } else {
      batches.push({
        key: item.id,
        title: item.title,
        kind: item.kind,
        danger: item.danger,
        senderRole: item.senderRole,
        items: [item],
      });
    }
  }
  return batches;
};

const startOfLocalDay = (offsetDays = 0): Date => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  if (offsetDays) d.setDate(d.getDate() - offsetDays);
  return d;
};

export const getDateFrom = (filter: NotificationDateFilter): string | undefined => {
  switch (filter) {
    case 'all':
      return undefined;
    case 'today':
      return startOfLocalDay().toISOString();
    case 'week':
      return startOfLocalDay(6).toISOString();
    case 'month':
      return startOfLocalDay(29).toISOString();
  }
};

/**
 * Which icon a notification wears. The wording is the notification service's now, but the
 * iconography is not on the wire, so it stays keyed by type here. A type missing from the map —
 * one the backend added and this app has not caught up with — still renders, as an alert.
 */
const KIND_BY_TYPE: Record<NotificationType, NotificationKind> = {
  [NotificationType.NEW_MESSAGE]: 'message',
  [NotificationType.MENTION_MESSAGE]: 'mention',
  [NotificationType.ASSIGN_USER_PERSONAL]: 'message',
  [NotificationType.UNASSIGN_USER_PERSONAL]: 'message',
  [NotificationType.DELETE_VESSEL]: 'vessel',
  [NotificationType.MENU_UPDATED]: 'menu',
  [NotificationType.UPCOMING_EVENT]: 'event',
  [NotificationType.ADDED_AS_PARTICIPANT_EVENT]: 'event',
  [NotificationType.EVENT_UPDATED]: 'event',
  [NotificationType.EVENT_CANCELED]: 'alert',
  [NotificationType.EVENT_RENEWED]: 'event',
  [NotificationType.REMOVED_FROM_EVENT]: 'event',
  [NotificationType.TASK_REMINDER]: 'task',
  [NotificationType.ADDED_AS_PARTICIPANT_TASK]: 'task',
  [NotificationType.ADDED_AS_SUPERVISOR]: 'task',
  [NotificationType.ADDED_AS_RESPONSIBLE]: 'task',
  [NotificationType.TASK_UPDATED]: 'task',
  [NotificationType.TASK_DELETED]: 'alert',
  [NotificationType.REMOVED_FROM_TASK]: 'task',
  [NotificationType.REPEATABLE_TASKS_CREATED]: 'task',
  [NotificationType.REPEATABLE_EVENTS_CREATED]: 'event',
  [NotificationType.ROLES_ASSIGNED]: 'message',
  [NotificationType.ROLES_REMOVED]: 'message',
  [NotificationType.INVITE_CREATED]: 'message',
  [NotificationType.INVITE_DECLINED]: 'message',
  [NotificationType.INVITE_CANCELED]: 'message',
  [NotificationType.REFERRAL_BONUS_APPLIED]: 'referral',
  [NotificationType.OWNERSHIP_TRANSFERRED]: 'vessel',
  [NotificationType.VESSEL_OWNER_LEFT]: 'vessel',
  [NotificationType.CREW_CONTRACT_EXPIRING]: 'alert',
  [NotificationType.CREW_CONTRACT_ENDED]: 'message',
  [NotificationType.CREW_CONTRACT_UPDATED]: 'message',
  [NotificationType.CREW_CONTRACT_CONVERTED]: 'message',
  [NotificationType.CREW_CONTRACT_SCHEDULED]: 'message',
  [NotificationType.DELETE_SERVICE_PROVIDER]: 'service',
  [NotificationType.FAILED_TO_SEND_EMAIL]: 'alert',
  [NotificationType.PAYMENT_FAILED]: 'payment',
  [NotificationType.PAYMENT_CANCELED]: 'payment',
  [NotificationType.PAYMENT_COMPLETED]: 'payment',
  [NotificationType.PAYMENT_NEEDS_APPROVAL]: 'payment',
  [NotificationType.PAYOUT_LINK_CREATED]: 'payment',
  [NotificationType.PAYOUT_EXPIRED]: 'payment',
  [NotificationType.PAYOUT_FAILED]: 'payment',
  [NotificationType.SUBSCRIPTION_UPDATED]: 'subscription',
  [NotificationType.SUBSCRIPTION_ACTIVATED]: 'subscription',
  [NotificationType.SUBSCRIPTION_TRIAL_GRANTED]: 'subscription',
  [NotificationType.SUBSCRIPTION_TRIAL_ENDING]: 'subscription',
  [NotificationType.SUBSCRIPTION_PAYMENT_FAILED]: 'subscription',
  [NotificationType.SUBSCRIPTION_DOWNGRADED]: 'subscription',
  [NotificationType.SUBSCRIPTION_CANCELED]: 'subscription',
  [NotificationType.SUBSCRIPTION_RESUMED]: 'subscription',
  [NotificationType.SUBSCRIPTION_PAYER_LEFT]: 'subscription',
  [NotificationType.CO_OWNER_SEAT_UNAVAILABLE]: 'subscription',
  [NotificationType.SEAT_PAYMENT_FAILED]: 'subscription',
};

const DANGER_TYPES = new Set<NotificationType>([
  NotificationType.TASK_DELETED,
  NotificationType.EVENT_CANCELED,
  NotificationType.INVITE_CANCELED,
  NotificationType.DELETE_VESSEL,
  NotificationType.DELETE_SERVICE_PROVIDER,
  NotificationType.FAILED_TO_SEND_EMAIL,
  NotificationType.PAYMENT_FAILED,
  NotificationType.PAYMENT_CANCELED,
  NotificationType.PAYOUT_FAILED,
  NotificationType.SUBSCRIPTION_PAYMENT_FAILED,
  NotificationType.SUBSCRIPTION_CANCELED,
  NotificationType.SUBSCRIPTION_DOWNGRADED,
  NotificationType.SUBSCRIPTION_PAYER_LEFT,
]);

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;

/**
 * The concrete change behind a `menu_updated` notification. Read only to colour a deleted
 * menu as destructive — the wording itself comes from the server.
 */
const getMenuAction = (data: unknown): MenuNotificationAction => {
  const action = isObject(data) ? data.action : undefined;
  return action === MenuNotificationAction.CREATED || action === MenuNotificationAction.DELETED
    ? action
    : MenuNotificationAction.UPDATED;
};

/** Task notifications that point at a single, still-navigable task. */
const LINKABLE_TASK_TYPES = new Set<NotificationType>([
  NotificationType.TASK_REMINDER,
  NotificationType.ADDED_AS_PARTICIPANT_TASK,
  NotificationType.ADDED_AS_SUPERVISOR,
  NotificationType.ADDED_AS_RESPONSIBLE,
  NotificationType.TASK_UPDATED,
]);

const extractTask = (event: NotificationEvent): NotificationItem['task'] => {
  const { type, data } = event;
  if (!LINKABLE_TASK_TYPES.has(type) || !isObject(data)) return undefined;
  const id = typeof data.id === 'string' ? data.id : undefined;
  if (!id) return undefined;
  return { id, title: typeof data.title === 'string' ? data.title : '' };
};

/**
 * Event notifications that point at a single, still-navigable event. Cancel and renew belong
 * here: the backend keeps the event in place and only flips its status.
 */
const LINKABLE_EVENT_TYPES = new Set<NotificationType>([
  NotificationType.UPCOMING_EVENT,
  NotificationType.ADDED_AS_PARTICIPANT_EVENT,
  NotificationType.EVENT_UPDATED,
  NotificationType.EVENT_CANCELED,
  NotificationType.EVENT_RENEWED,
]);

const extractEvent = (event: NotificationEvent): NotificationItem['event'] => {
  const { type, data } = event;
  if (!LINKABLE_EVENT_TYPES.has(type) || !isObject(data)) return undefined;
  const id = typeof data.id === 'string' ? data.id : undefined;
  if (!id) return undefined;
  return { id, title: typeof data.title === 'string' ? data.title : '' };
};

/**
 * Menu notifications point at the event's menu page, which stays a valid target for
 * every action — including a deleted menu, where the page lists what is left.
 */
const extractMenu = (event: NotificationEvent): NotificationItem['menu'] => {
  const { type, data } = event;
  if (type !== NotificationType.MENU_UPDATED || !isObject(data)) return undefined;
  const eventId = typeof data.event_id === 'string' ? data.event_id : undefined;
  return eventId ? { eventId } : undefined;
};

/**
 * Notifications that name a chat room. A message carries the room under `message.room_id`;
 * add/remove-from-chat carries it at the top level (`buildChatLink`, notification service
 * `internal/app/links/links.go`, reads both).
 */
const CHAT_ROOM_TYPES = new Set<NotificationType>([
  NotificationType.NEW_MESSAGE,
  NotificationType.MENTION_MESSAGE,
  NotificationType.ASSIGN_USER_PERSONAL,
  NotificationType.UNASSIGN_USER_PERSONAL,
]);

const extractChatRoom = (event: NotificationEvent): NotificationItem['chatRoom'] => {
  if (!CHAT_ROOM_TYPES.has(event.type)) return undefined;
  const { data } = event;
  if (!isObject(data)) return undefined;
  const message = data.message;
  const roomId =
    typeof data.room_id === 'string'
      ? data.room_id
      : isObject(message) && typeof message.room_id === 'string'
        ? message.room_id
        : undefined;
  if (!roomId) return undefined;
  const name = typeof data.room_name === 'string' ? data.room_name : '';
  return { id: roomId, name };
};

const extractInvite = (event: NotificationEvent): NotificationItem['invite'] => {
  if (event.type !== NotificationType.INVITE_CREATED || !isObject(event.data)) return undefined;
  const { id, service_id, service_name, role_name, role, expires_at, additional_guests } =
    event.data;
  if (typeof id !== 'string' || typeof service_id !== 'string') return undefined;
  return {
    id,
    serviceId: service_id,
    serviceName: typeof service_name === 'string' ? service_name : '',
    roleName: typeof role_name === 'string' ? role_name : typeof role === 'string' ? role : '',
    expiresAt: typeof expires_at === 'string' ? expires_at : undefined,
    additionalGuests: typeof additional_guests === 'number' ? additional_guests : 0,
  };
};

/**
 * The server's link as an in-app path.
 *
 * Dropped for `unassign_user_personal`: the notification service links every chat type to
 * `/inbox/<room_id>`, but that one says the reader was just removed from the room, so its
 * target is a room they can no longer open.
 */
const resolveLink = (type: NotificationType, link?: string): string | undefined =>
  type === NotificationType.UNASSIGN_USER_PERSONAL ? undefined : (toInAppPath(link) ?? undefined);

/**
 * The format this app renders: the notification service supplies the title and the body, per
 * channel (`Event.Render(..., ChannelFrontend)`, `pkg/dto/ws.go`). There is no second copy of
 * that wording here, so a notification that does not carry it cannot be shown — it says so
 * instead of being dropped or guessed at from the payload.
 *
 * In practice that is a row written before the service started rendering for this channel, or
 * one whose `event` blob did not decode at all, which arrives as null.
 */
type RenderedEvent = NotificationEvent & { subject: string; body: string };

const isRenderable = (event: NotificationEvent | null): event is RenderedEvent =>
  !!event?.subject && !!event.body;

const toUnreadableItem = (notification: Notification): NotificationItem => ({
  id: notification.id,
  kind: 'alert',
  title: 'Notification unavailable',
  description: 'This notification is in a format this app can’t display.',
  time: formatRelativeTime(notification.date),
  dateKey: toLocalDateKey(notification.date),
  read: notification.read,
  danger: true,
  senderRole: notification.senderRole,
});

export const toPanelItem = (notification: Notification): NotificationItem => {
  const { event } = notification;
  if (!isRenderable(event)) return toUnreadableItem(notification);

  const { type, data, subject, body, link } = event;
  const menuDeleted =
    type === NotificationType.MENU_UPDATED &&
    getMenuAction(data) === MenuNotificationAction.DELETED;

  return {
    id: notification.id,
    // A type this app does not know still renders; only its icon falls back.
    kind: KIND_BY_TYPE[type] ?? 'alert',
    title: subject,
    description: body,
    // Absolute URL from the server; only the path survives, so the router navigates instead
    // of reloading the app.
    link: resolveLink(type, link),
    time: formatRelativeTime(notification.date),
    dateKey: toLocalDateKey(notification.date),
    read: notification.read,
    danger: DANGER_TYPES.has(type) || menuDeleted,
    senderRole: notification.senderRole,
    chatRoom: extractChatRoom(event),
    task: extractTask(event),
    event: extractEvent(event),
    menu: extractMenu(event),
    invite: extractInvite(event),
  };
};

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

export const formatRelativeTime = (iso: string, now: Date = new Date()): string => {
  const date = new Date(iso);
  const diff = now.getTime() - date.getTime();
  if (diff < MINUTE_MS) return 'now';
  if (diff < HOUR_MS) return `${Math.floor(diff / MINUTE_MS)}m`;
  if (diff < DAY_MS) return `${Math.floor(diff / HOUR_MS)}h`;
  return date.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  });
};

export const toLocalDateKey = (iso: string): string => {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const offsetDateKey = (days: number): string => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return toLocalDateKey(d.toISOString());
};

export const formatDateDivider = (dateKey: string, now: Date = new Date()): string => {
  const todayKey = toLocalDateKey(now.toISOString());
  if (dateKey === todayKey) return 'Today';
  if (dateKey === offsetDateKey(1)) return 'Yesterday';

  const d = new Date(`${dateKey}T00:00:00`);
  return d.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'long',
    ...(d.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  });
};
